/**
 * 新規登録ページ - Neo-Futuristic HUD
 * Step1: アカウント情報入力（氏名・メール）
 * Step2: パスワード設定
 * Step3: 利用形態選択（個人 or 組織）
 * Step4: メール確認
 * COMPLETE: 登録完了
 */

import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { signUp, confirmSignUp } from '../services/auth';

const STEPS = {
  ACCOUNT: 1,
  PASSWORD: 2,
  ORG_TYPE: 3,
  VERIFY: 4,
  COMPLETE: 5,
};

// HUD装飾: フォームカードの四隅ブラケット
function InstrumentBrackets() {
  return (
    <>
      <div className="absolute top-0 left-0 border-l-4 border-t-4 border-primary w-8 h-8 pointer-events-none" />
      <div className="absolute top-0 right-0 border-r-4 border-t-4 border-primary w-8 h-8 pointer-events-none" />
      <div className="absolute bottom-0 left-0 border-l-4 border-b-4 border-primary w-8 h-8 pointer-events-none" />
      <div className="absolute bottom-0 right-0 border-r-4 border-b-4 border-primary w-8 h-8 pointer-events-none" />
    </>
  );
}

// 入力フィールド: reticleコーナー + アンダーライン
function HudInput({ label, icon, ...props }) {
  return (
    <div className="reticle-group group relative flex flex-col gap-2">
      <div className="reticle-corner top-0 left-0 border-l-2 border-t-2" style={{ top: 0, left: 0 }} />
      <div className="reticle-corner top-0 right-0 border-r-2 border-t-2" style={{ top: 0, right: 0 }} />
      <div className="reticle-corner bottom-0 left-0 border-l-2 border-b-2" style={{ bottom: 0, left: 0 }} />
      <div className="reticle-corner bottom-0 right-0 border-r-2 border-b-2" style={{ bottom: 0, right: 0 }} />
      <div className="px-4 pt-3 pb-1">
        <label className="font-label-caps text-label-caps text-primary/60 block mb-1">{label}</label>
        <div className="relative">
          {icon && (
            <span className="material-symbols-outlined absolute left-0 top-1/2 -translate-y-1/2 text-primary/40 group-focus-within:text-primary transition-colors text-xl select-none">
              {icon}
            </span>
          )}
          <input
            className="w-full bg-transparent border-none focus:ring-0 py-2 pl-8 pr-2 text-on-surface font-body-sm placeholder:text-outline outline-none"
            {...props}
          />
        </div>
      </div>
    </div>
  );
}

// 送信ボタン: 四角 + box-shadowグロー
function HudButton({ children, loading, icon, variant, ...props }) {
  if (variant === 'ghost') {
    return (
      <button
        className="rounded-none w-full h-12 bg-transparent border border-primary/50 text-primary font-label-caps text-label-caps tracking-[0.2em] hover:border-primary hover:bg-primary/10 active:scale-[0.98] transition-all flex items-center justify-center gap-3 group relative overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed"
        {...props}
      >
        {icon && (
          <span className="material-symbols-outlined text-[20px] group-hover:-translate-x-1 transition-transform" style={{ fontSize: '20px' }}>
            {icon}
          </span>
        )}
        <span>{children}</span>
      </button>
    );
  }

  return (
    <button
      className="rounded-none w-full h-14 bg-primary text-on-primary font-label-caps text-label-caps tracking-[0.2em] hover:shadow-[0_0_30px_rgba(162,201,255,0.9)] active:scale-[0.98] transition-all flex items-center justify-center gap-3 group relative overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed"
      {...props}
    >
      <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
      {loading ? (
        <span className="flex items-center gap-2">
          <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          PROCESSING...
        </span>
      ) : (
        <>
          <span>{children}</span>
          {icon && (
            <span className="material-symbols-outlined text-[20px] group-hover:translate-x-1 transition-transform" style={{ fontSize: '20px' }}>
              {icon}
            </span>
          )}
        </>
      )}
    </button>
  );
}

// エラー表示
function HudAlert({ message }) {
  if (!message) return null;
  return (
    <div className="border border-error/50 bg-error-container/30 px-4 py-2 flex items-center gap-3">
      <span className="material-symbols-outlined text-error text-base select-none">warning</span>
      <span className="font-label-caps text-[11px] text-error tracking-wider">{message}</span>
    </div>
  );
}

// HUDステップインジケーター
function HudStepIndicator({ step }) {
  const steps = [
    { num: '01', label: 'ACCOUNT' },
    { num: '02', label: 'PASSWORD' },
    { num: '03', label: 'ORG_TYPE' },
    { num: '04', label: 'VERIFY' },
  ];

  return (
    <div className="flex items-center justify-center gap-0 mb-6">
      {steps.map((s, i) => {
        const stepNum = i + 1;
        const isCompleted = step > stepNum;
        const isActive = step === stepNum;

        return (
          <React.Fragment key={i}>
            <div className="flex flex-col items-center gap-1">
              <div className={`
                flex items-center justify-center w-7 h-7 border
                ${isCompleted ? 'border-secondary bg-secondary/10' : isActive ? 'border-primary bg-primary/10' : 'border-outline/30'}
              `}>
                {isCompleted ? (
                  <span className="material-symbols-outlined text-secondary" style={{ fontSize: '14px' }}>check</span>
                ) : (
                  <span className={`font-label-caps text-[10px] ${isActive ? 'text-primary' : 'text-outline/50'}`}>
                    {s.num}
                  </span>
                )}
              </div>
              <span className={`font-label-caps text-[9px] tracking-[0.12em] ${isCompleted ? 'text-secondary/70' : isActive ? 'text-primary/70' : 'text-outline/40'}`}>
                {s.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div className={`w-8 h-[1px] mb-5 ${step > stepNum ? 'bg-secondary/50' : 'bg-outline/20'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// 共通背景レイヤー
function HudBackground() {
  return (
    <div className="fixed inset-0 z-0">
      <img
        alt="Tactical Drone Fleet"
        className="w-full h-full object-cover"
        src="https://lh3.googleusercontent.com/aida-public/AB6AXuCBRp9Rp9b_9FZD6pgs42HKarUwW9BzoZe45sKxLFhyGVQSPYHrlWopngzNaO87ZU5jasOVmT_OJGRE07M6oT787R_6tg6_9nezcCGRamtkONcwoQmRw6wBzVWJ7P7huualqYfzSjNY409rmqJucpzG3BHhOADzhhjrjNmfhJ8RpIqoE1G4oDrSLFFwY0na1L_Sndiy4po4YzbzVyLZoSC3lKFRn9sEMZ9Z4oA8hCCccysp1xFiiS9qDGu_4YRos4X9CXyWmn10Yg"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950/40 via-transparent to-slate-950/80" />
      <div className="absolute inset-0 grid-bg" />
      <div className="absolute inset-0 scanline pointer-events-none" />
      <div className="absolute inset-0 opacity-10 animate-scan bg-gradient-to-b from-transparent via-primary/20 to-transparent h-1/2" />
    </div>
  );
}

// 共通HUD上部装飾
function HudTopDecor() {
  return (
    <div className="hud-fade-in absolute flex justify-between items-start pointer-events-none" style={{ top: '32px', left: '32px', right: '32px', animationDelay: '0.6s' }}>
      <div className="hidden lg:flex flex-col gap-2">
        <span className="font-label-caps text-label-caps text-primary opacity-60 blur-[1px]">MISSION_STATUS</span>
        <div className="flex items-center gap-3">
          <div className="w-2 h-2 rounded-full bg-secondary animate-pulse" />
          <span className="font-headline-md text-secondary blur-[1.5px] opacity-70">▰▱■▰_▱■▰▱</span>
        </div>
      </div>
      <div className="hidden lg:flex flex-col items-end gap-2 text-right">
        <span className="font-label-caps text-label-caps text-primary opacity-60 blur-[1px]">TELEMETRY_LINK</span>
        <span className="font-body-sm text-primary/80">▰▱ ⧖ ▫ ■ ■ ▫ ⧖</span>
      </div>
    </div>
  );
}

// 共通HUD下部装飾
function HudBottomDecor() {
  return (
    <div className="hud-fade-in absolute pointer-events-none" style={{ bottom: '32px', left: '32px', right: '32px', animationDelay: '0.6s' }}>
      <div className="flex justify-between items-end">
        <div className="hidden lg:flex flex-col gap-1">
          <span className="font-label-caps text-[10px] text-primary/30 uppercase blur-[1px]">System Integrity</span>
          <div className="flex gap-1">
            <div className="w-1 h-3 bg-secondary" />
            <div className="w-1 h-3 bg-secondary" />
            <div className="w-1 h-3 bg-secondary" />
            <div className="w-1 h-3 bg-secondary" />
            <div className="w-1 h-3 bg-secondary/30" />
          </div>
        </div>
        <div className="text-right">
          <span className="font-label-caps text-[10px] text-primary/40 block">θ.9 ■ X7-β ▰▱</span>
          <span className="font-label-caps text-[10px] text-primary/40 block">⧖ ▫ ■■-09-Ω</span>
        </div>
      </div>
    </div>
  );
}

// ステップラベル
const STEP_LABELS = {
  [1]: 'New Operator Registration',
  [2]: 'Set Encryption Key',
  [3]: 'Select Operation Mode',
  [4]: 'Identity Verification',
};

export default function Register() {
  const navigate = useNavigate();
  const [step, setStep] = useState(STEPS.ACCOUNT);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const sectionRef = useRef(null);

  const navigateOut = useCallback((path) => {
    const el = sectionRef.current;
    if (el) {
      const anim = el.animate(
        [
          { opacity: 1, transform: 'perspective(900px) rotateX(0deg) translateY(0) scale(1)' },
          { opacity: 0, transform: 'perspective(900px) rotateX(30deg) translateY(48px) scale(0.96)' },
        ],
        { duration: 400, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'both' }
      );
      anim.onfinish = () => navigate(path);
    } else {
      navigate(path);
    }
  }, [navigate]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [form, setForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    orgType: '',
    orgName: '',
    verificationCode: '',
  });

  const updateForm = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const passwordChecks = {
    length: form.password.length >= 8,
    uppercase: /[A-Z]/.test(form.password),
    lowercase: /[a-z]/.test(form.password),
    number: /[0-9]/.test(form.password),
    match: form.password === form.confirmPassword && form.confirmPassword.length > 0,
  };
  const isPasswordValid = Object.values(passwordChecks).every(Boolean);

  const handleAccountNext = (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      setError('すべての項目を入力してください');
      return;
    }
    setError('');
    setStep(STEPS.PASSWORD);
  };

  const handlePasswordNext = (e) => {
    e.preventDefault();
    if (!isPasswordValid) {
      setError('パスワードの要件を満たしてください');
      return;
    }
    setError('');
    setStep(STEPS.ORG_TYPE);
  };

  const handleOrgTypeNext = async (e) => {
    e.preventDefault();
    if (!form.orgType) {
      setError('利用形態を選択してください');
      return;
    }
    if (form.orgType === 'organization' && !form.orgName.trim()) {
      setError('組織名を入力してください');
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      await signUp({
        email: form.email,
        password: form.password,
        name: form.name,
        orgType: form.orgType,
      });
      setStep(STEPS.VERIFY);
    } catch (err) {
      if (err.code === 'UsernameExistsException' || err.name === 'UsernameExistsException') {
        setError('このメールアドレスはすでに登録されています');
      } else {
        setError(err.message || '登録に失敗しました');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!form.verificationCode) {
      setError('確認コードを入力してください');
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      await confirmSignUp(form.email, form.verificationCode);
      sessionStorage.setItem('pendingRegistration', JSON.stringify({
        name: form.name,
        orgType: form.orgType,
        orgName: form.orgType === 'organization' ? form.orgName : form.name,
      }));
      setStep(STEPS.COMPLETE);
    } catch (err) {
      if (err.code === 'CodeMismatchException') {
        setError('確認コードが正しくありません');
      } else if (err.code === 'ExpiredCodeException') {
        setError('確認コードの有効期限が切れています。再送信してください');
      } else {
        setError(err.message || '確認に失敗しました');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsLoading(true);
    setError('');
    try {
      const { resendConfirmationCode } = await import('../services/auth');
      await resendConfirmationCode(form.email);
      alert('確認コードを再送信しました');
    } catch (err) {
      setError('再送信に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // COMPLETE画面（ステップ外）
  if (step === STEPS.COMPLETE) {
    return (
      <div className="bg-background text-on-background font-body-lg min-h-screen overflow-hidden">
        <HudBackground />
        <main className="relative z-10 flex flex-col items-center justify-center min-h-screen p-gutter">
          <HudTopDecor />

          <div className="relative w-full max-w-lg">
            <div className="hud-fade-in absolute -inset-12 border-2 border-secondary/10 rounded-full pointer-events-none border-dashed animate-[spin_60s_linear_infinite]" style={{ animationDelay: '0.8s' }} />

            <section
              ref={sectionRef}
              className="hud-rise glass-panel relative p-8 flex flex-col overflow-hidden"
              style={{ height: 'min(88vh, 620px)' }}
            >
              <InstrumentBrackets />

              {/* ヘッダー */}
              <div className="text-center mb-4 shrink-0">
                <h1 className="font-headline-xl text-headline-xl text-primary tracking-[0.2em] italic font-black uppercase mb-1">
                  AIRGROW
                </h1>
                <div className="flex items-center justify-center gap-3">
                  <div className="h-[1px] w-8 bg-primary/30" />
                  <p className="font-label-caps text-[10px] text-primary/70 tracking-[0.4em]">FLIGNT PLANNER V0.01</p>
                  <div className="h-[1px] w-8 bg-primary/30" />
                </div>
              </div>

              {/* 完了コンテンツ: 縦中央配置 */}
              <div className="flex-1 flex flex-col items-center justify-center text-center gap-5">
                <div className="relative flex items-center justify-center w-20 h-20 border-2 border-secondary shadow-[0_0_30px_rgba(139,217,123,0.4)]">
                  <div className="absolute top-0 left-0 border-l-2 border-t-2 border-secondary w-4 h-4" />
                  <div className="absolute bottom-0 right-0 border-r-2 border-b-2 border-secondary w-4 h-4" />
                  <span className="material-symbols-outlined text-secondary" style={{ fontSize: '40px' }}>verified</span>
                </div>

                <div className="bg-secondary/10 border border-secondary/30 py-1 px-4 inline-block">
                  <span className="font-label-caps text-[11px] text-secondary tracking-widest uppercase">
                    Registration Complete
                  </span>
                </div>

                <p className="font-body-sm text-on-surface-variant">
                  {form.orgType === 'organization'
                    ? `「${form.orgName}」として登録されました。`
                    : '個人アカウントが作成されました。'}
                  <br />
                  ログインしてドローン管理を始めましょう。
                </p>
              </div>

              {/* ボタン */}
              <div className="shrink-0">
                <HudButton icon="rocket_launch" onClick={() => navigateOut('/login')}>
                  LAUNCH_SYSTEM
                </HudButton>
              </div>
            </section>
          </div>

          <HudBottomDecor />
        </main>
      </div>
    );
  }

  // Steps 1〜4: 固定サイズダイアログ
  return (
    <div className="bg-background text-on-background font-body-lg min-h-screen overflow-hidden">
      <HudBackground />
      <main className="relative z-10 flex flex-col items-center justify-center min-h-screen p-gutter">
        <HudTopDecor />

        <div className="relative w-full max-w-lg">
          <div className="hud-fade-in absolute -inset-12 border-2 border-primary/10 rounded-full pointer-events-none border-dashed animate-[spin_60s_linear_infinite]" style={{ animationDelay: '0.8s' }} />

          {/* 固定高さのダイアログ */}
          <section
            ref={sectionRef}
            className="hud-rise glass-panel relative p-8 flex flex-col overflow-hidden"
            style={{ height: 'min(88vh, 620px)' }}
          >
            <InstrumentBrackets />

            {/* 共通ヘッダー (shrink-0) */}
            <div className="text-center mb-4 shrink-0">
              <h1 className="font-headline-xl text-headline-xl text-primary tracking-[0.2em] italic font-black uppercase mb-1">
                AIRGROW
              </h1>
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="h-[1px] w-8 bg-primary/30" />
                <p className="font-label-caps text-[10px] text-primary/70 tracking-[0.4em]">FLIGNT PLANNER V0.01</p>
                <div className="h-[1px] w-8 bg-primary/30" />
              </div>
              <div className="bg-primary/10 py-1 px-4 inline-block">
                <span className="font-label-caps text-[11px] text-primary tracking-widest uppercase">
                  {STEP_LABELS[step]}
                </span>
              </div>
            </div>

            <HudStepIndicator step={step} />

            {/* コンテンツ領域 (flex-1) */}
            <div className="flex-1 flex flex-col min-h-0">

              {/* Step1: 氏名・メール */}
              {step === STEPS.ACCOUNT && (
                <form onSubmit={handleAccountNext} className="flex flex-col gap-4 h-full">
                  <HudAlert message={error} />
                  <div className="flex flex-col gap-3 flex-1">
                    <HudInput
                      label="OPERATOR_NAME"
                      icon="person"
                      type="text"
                      value={form.name}
                      onChange={(e) => updateForm('name', e.target.value)}
                      placeholder="例：岡崎 年弘"
                      required
                      autoFocus
                    />
                    <HudInput
                      label="MAIL_ADDRESS"
                      icon="mail"
                      type="email"
                      value={form.email}
                      onChange={(e) => updateForm('email', e.target.value)}
                      placeholder="operator@example.com"
                      required
                    />
                  </div>
                  {/* Step2〜4 の [BACK][NEXT] と同じ横並びレイアウト */}
                  <div className="flex gap-3 shrink-0">
                    <button
                      type="button"
                      onClick={() => navigateOut('/login')}
                      style={{ flex: 1 }}
                      className="rounded-none h-12 bg-transparent border border-primary/50 text-primary font-label-caps text-label-caps tracking-[0.2em] hover:border-primary hover:bg-primary/10 active:scale-[0.98] transition-all flex items-center justify-center gap-2 group"
                    >
                      <span className="material-symbols-outlined text-[18px] group-hover:-translate-x-1 transition-transform" style={{ fontSize: '18px' }}>login</span>
                      <span>LOGIN</span>
                    </button>
                    <HudButton type="submit" icon="arrow_forward" disabled={!form.name || !form.email} style={{ flex: 2 }}>
                      NEXT_STEP
                    </HudButton>
                  </div>
                </form>
              )}

              {/* Step2: パスワード設定 */}
              {step === STEPS.PASSWORD && (
                <form onSubmit={handlePasswordNext} className="flex flex-col gap-4 h-full">
                  <HudAlert message={error} />
                  <div className="flex flex-col gap-3 flex-1">
                    {/* パスワード（表示切替付き） */}
                    <div className="reticle-group group relative flex flex-col gap-2">
                      <div className="reticle-corner border-l-2 border-t-2" style={{ top: 0, left: 0 }} />
                      <div className="reticle-corner border-r-2 border-t-2" style={{ top: 0, right: 0 }} />
                      <div className="reticle-corner border-l-2 border-b-2" style={{ bottom: 0, left: 0 }} />
                      <div className="reticle-corner border-r-2 border-b-2" style={{ bottom: 0, right: 0 }} />
                      <div className="px-4 pt-3 pb-1">
                        <label className="font-label-caps text-label-caps text-primary/60 block mb-1">ENCRYPTION_KEY</label>
                        <div className="relative">
                          <span className="material-symbols-outlined absolute left-0 top-1/2 -translate-y-1/2 text-primary/40 text-xl select-none">lock</span>
                          <input
                            type={showPassword ? 'text' : 'password'}
                            value={form.password}
                            onChange={(e) => updateForm('password', e.target.value)}
                            placeholder="8文字以上、大小英字・数字含む"
                            required
                            autoFocus
                            className="w-full bg-transparent border-none focus:ring-0 py-2 pl-8 pr-10 text-on-surface font-body-sm placeholder:text-outline outline-none"
                          />
                          <button type="button" onClick={() => setShowPassword(v => !v)}
                            className="absolute right-0 top-1/2 -translate-y-1/2 text-primary/40 hover:text-primary transition-colors">
                            <span className="material-symbols-outlined text-xl">{showPassword ? 'visibility_off' : 'visibility'}</span>
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* パスワード確認（表示切替付き） */}
                    <div className="reticle-group group relative flex flex-col gap-2">
                      <div className="reticle-corner border-l-2 border-t-2" style={{ top: 0, left: 0 }} />
                      <div className="reticle-corner border-r-2 border-t-2" style={{ top: 0, right: 0 }} />
                      <div className="reticle-corner border-l-2 border-b-2" style={{ bottom: 0, left: 0 }} />
                      <div className="reticle-corner border-r-2 border-b-2" style={{ bottom: 0, right: 0 }} />
                      <div className="px-4 pt-3 pb-1">
                        <label className="font-label-caps text-label-caps text-primary/60 block mb-1">CONFIRM_KEY</label>
                        <div className="relative">
                          <span className="material-symbols-outlined absolute left-0 top-1/2 -translate-y-1/2 text-primary/40 text-xl select-none">lock_reset</span>
                          <input
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={form.confirmPassword}
                            onChange={(e) => updateForm('confirmPassword', e.target.value)}
                            placeholder="パスワード再入力"
                            required
                            className="w-full bg-transparent border-none focus:ring-0 py-2 pl-8 pr-10 text-on-surface font-body-sm placeholder:text-outline outline-none"
                          />
                          <button type="button" onClick={() => setShowConfirmPassword(v => !v)}
                            className="absolute right-0 top-1/2 -translate-y-1/2 text-primary/40 hover:text-primary transition-colors">
                            <span className="material-symbols-outlined text-xl">{showConfirmPassword ? 'visibility_off' : 'visibility'}</span>
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* パスワード要件チェックリスト: 常時表示 */}
                    <div className="border border-outline/20 bg-surface-container/40 px-4 py-2">
                      <p className="font-label-caps text-[10px] text-primary/50 tracking-[0.2em] mb-2">PASSWORD_REQUIREMENTS</p>
                      <div className="grid grid-cols-2 gap-1">
                        {[
                          { ok: passwordChecks.length, label: '8文字以上' },
                          { ok: passwordChecks.uppercase, label: '大文字 (A-Z)' },
                          { ok: passwordChecks.lowercase, label: '小文字 (a-z)' },
                          { ok: passwordChecks.number, label: '数字 (0-9)' },
                          { ok: passwordChecks.match, label: 'パスワード一致' },
                        ].map(({ ok, label }) => (
                          <div key={label} className={`flex items-center gap-1.5 ${ok ? 'text-secondary' : 'text-outline/50'}`}>
                            <span className="material-symbols-outlined" style={{ fontSize: '13px' }}>
                              {ok ? 'check_circle' : 'radio_button_unchecked'}
                            </span>
                            <span className="font-label-caps text-[10px] tracking-[0.1em]">{label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3 shrink-0">
                    <HudButton type="button" variant="ghost" icon="arrow_back"
                      onClick={() => { setError(''); setStep(STEPS.ACCOUNT); }}
                      style={{ flex: 1 }}>
                      BACK
                    </HudButton>
                    <HudButton type="submit" icon="arrow_forward" disabled={!isPasswordValid} style={{ flex: 2 }}>
                      NEXT_STEP
                    </HudButton>
                  </div>
                </form>
              )}

              {/* Step3: 利用形態選択 */}
              {step === STEPS.ORG_TYPE && (
                <form onSubmit={handleOrgTypeNext} className="flex flex-col gap-3 h-full">
                  <HudAlert message={error} />
                  <div className="flex flex-col gap-2 flex-1">
                    {/* 個人利用 */}
                    <button
                      type="button"
                      onClick={() => updateForm('orgType', 'personal')}
                      className={`
                        relative flex items-start gap-3 p-4 text-left border transition-all
                        ${form.orgType === 'personal'
                          ? 'border-primary bg-primary/10 shadow-[0_0_20px_rgba(162,201,255,0.15)]'
                          : 'border-outline/30 hover:border-outline/60 bg-surface-container/30'
                        }
                      `}
                    >
                      {form.orgType === 'personal' && <div className="absolute top-0 left-0 border-l-2 border-t-2 border-primary w-3 h-3" />}
                      {form.orgType === 'personal' && <div className="absolute bottom-0 right-0 border-r-2 border-b-2 border-primary w-3 h-3" />}
                      <div className={`flex-shrink-0 flex items-center justify-center w-9 h-9 border ${form.orgType === 'personal' ? 'border-primary/60 bg-primary/10' : 'border-outline/30'}`}>
                        <span className="material-symbols-outlined text-primary/70" style={{ fontSize: '20px' }}>person</span>
                      </div>
                      <div className="flex-1">
                        <div className={`font-label-caps text-[11px] tracking-[0.15em] mb-0.5 ${form.orgType === 'personal' ? 'text-primary' : 'text-on-surface'}`}>
                          PERSONAL_MODE
                        </div>
                        <div className="font-body-sm text-[12px] text-on-surface-variant leading-snug">
                          自分だけでドローンを管理します。後から組織にアップグレードできます。
                        </div>
                      </div>
                      <div className={`flex-shrink-0 w-4 h-4 border mt-0.5 flex items-center justify-center ${form.orgType === 'personal' ? 'border-primary' : 'border-outline/40'}`}>
                        {form.orgType === 'personal' && <div className="w-2 h-2 bg-primary" />}
                      </div>
                    </button>

                    {/* 組織利用 */}
                    <button
                      type="button"
                      onClick={() => updateForm('orgType', 'organization')}
                      className={`
                        relative flex items-start gap-3 p-4 text-left border transition-all
                        ${form.orgType === 'organization'
                          ? 'border-primary bg-primary/10 shadow-[0_0_20px_rgba(162,201,255,0.15)]'
                          : 'border-outline/30 hover:border-outline/60 bg-surface-container/30'
                        }
                      `}
                    >
                      {form.orgType === 'organization' && <div className="absolute top-0 left-0 border-l-2 border-t-2 border-primary w-3 h-3" />}
                      {form.orgType === 'organization' && <div className="absolute bottom-0 right-0 border-r-2 border-b-2 border-primary w-3 h-3" />}
                      <div className={`flex-shrink-0 flex items-center justify-center w-9 h-9 border ${form.orgType === 'organization' ? 'border-primary/60 bg-primary/10' : 'border-outline/30'}`}>
                        <span className="material-symbols-outlined text-primary/70" style={{ fontSize: '20px' }}>corporate_fare</span>
                      </div>
                      <div className="flex-1">
                        <div className={`font-label-caps text-[11px] tracking-[0.15em] mb-0.5 ${form.orgType === 'organization' ? 'text-primary' : 'text-on-surface'}`}>
                          ORGANIZATION_MODE
                        </div>
                        <div className="font-body-sm text-[12px] text-on-surface-variant leading-snug">
                          チームでドローンを管理します。メンバーを招待して共同管理できます。
                        </div>
                      </div>
                      <div className={`flex-shrink-0 w-4 h-4 border mt-0.5 flex items-center justify-center ${form.orgType === 'organization' ? 'border-primary' : 'border-outline/40'}`}>
                        {form.orgType === 'organization' && <div className="w-2 h-2 bg-primary" />}
                      </div>
                    </button>

                    {/* 組織名入力: 常時レンダリング、非選択時は invisible で領域確保 */}
                    <div className={form.orgType === 'organization' ? '' : 'invisible'}>
                      <HudInput
                        label="ORGANIZATION_NAME"
                        icon="corporate_fare"
                        type="text"
                        value={form.orgName}
                        onChange={(e) => updateForm('orgName', e.target.value)}
                        placeholder="例：株式会社NTT e-Drone Technology"
                        required={form.orgType === 'organization'}
                        autoFocus={form.orgType === 'organization'}
                      />
                    </div>
                  </div>

                  <div className="flex gap-3 shrink-0">
                    <HudButton type="button" variant="ghost" icon="arrow_back"
                      onClick={() => { setError(''); setStep(STEPS.PASSWORD); }}
                      style={{ flex: 1 }}>
                      BACK
                    </HudButton>
                    <HudButton type="submit" loading={isLoading} icon="send"
                      disabled={isLoading || !form.orgType} style={{ flex: 2 }}>
                      REGISTER
                    </HudButton>
                  </div>
                </form>
              )}

              {/* Step4: メール確認コード */}
              {step === STEPS.VERIFY && (
                <form onSubmit={handleVerify} className="flex flex-col gap-4 h-full">
                  <HudAlert message={error} />

                  {/* メール送信先 */}
                  <div className="border border-outline/20 bg-surface-container/40 px-4 py-3 flex items-center gap-3 shrink-0">
                    <span className="material-symbols-outlined text-primary/60 text-base select-none">mail</span>
                    <div>
                      <p className="font-label-caps text-[10px] text-primary/50 tracking-[0.15em] mb-0.5">VERIFICATION_TARGET</p>
                      <p className="font-body-sm text-[13px] text-on-surface">{form.email}</p>
                    </div>
                  </div>

                  {/* 確認コード入力 */}
                  <div className="my-auto">
                    <div className="reticle-group group relative flex flex-col gap-2">
                      <div className="reticle-corner border-l-2 border-t-2" style={{ top: 0, left: 0 }} />
                      <div className="reticle-corner border-r-2 border-t-2" style={{ top: 0, right: 0 }} />
                      <div className="reticle-corner border-l-2 border-b-2" style={{ bottom: 0, left: 0 }} />
                      <div className="reticle-corner border-r-2 border-b-2" style={{ bottom: 0, right: 0 }} />
                      <div className="px-4 pt-3 pb-1">
                        <label className="font-label-caps text-label-caps text-primary/60 block mb-1">AUTH_CODE</label>
                        <input
                          type="text"
                          value={form.verificationCode}
                          onChange={(e) => updateForm('verificationCode', e.target.value)}
                          placeholder="000000"
                          maxLength={6}
                          required
                          autoFocus
                          className="w-full bg-transparent border-none focus:ring-0 py-2 text-on-surface font-headline-md text-center tracking-[0.5em] placeholder:text-outline/40 outline-none"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 shrink-0">
                    <HudButton type="submit" loading={isLoading} icon="verified">
                      VERIFY_IDENTITY
                    </HudButton>
                    <div className="flex justify-center items-center gap-2">
                      <span className="font-label-caps text-[10px] text-primary/40">
                        コードが届かない場合は
                      </span>
                      <button type="button" onClick={handleResendCode}
                        className="font-label-caps text-[10px] text-primary hover:text-on-primary-container transition-colors border-b border-primary/40 hover:border-primary">
                        再送信
                      </button>
                    </div>
                  </div>
                </form>
              )}

            </div>
          </section>
        </div>

        <HudBottomDecor />
      </main>
    </div>
  );
}
